import { Link } from "react-router-dom";
import { ArrowRight, Building2, Briefcase, Scale, ShoppingBag, HeartHandshake } from "lucide-react";

const AUDIENCES = [
  {
    id: "01",
    icon: Building2,
    category: "PYME",
    headline: "Para dueños de PYME que cumplen sin equipo legal interno",
    text:
      "Escaneas tu sitio, te aparecen los hallazgos en lenguaje de humano, generas los documentos en 7 minutos y los publicas. Sin abogados ni cotizaciones por hora.",
    bullets: ["Score gratuito · 3 análisis / mes", "Pro $14.990 CLP · pago único", "Soporte por email en menos de 24h hábiles"],
    cta: { label: "Empezar gratis", to: "/" },
  },
  {
    id: "02",
    icon: Briefcase,
    category: "Agencias",
    headline: "Para agencias digitales que cumplen Ley 21.719 para sus clientes",
    text:
      "Genera política, RAT y Cláusulas Contractuales para cada cliente, deja el deep scan automático corriendo, recibe alertas cuando algo cambia. Sin tener que actualizar manualmente.",
    bullets: ["Deep scan multi-página por cliente", "Documentos parametrizables por marca", "Alertas regulatorias por email"],
    cta: { label: "Ver Pro", to: "/modulo-pro" },
  },
  {
    id: "03",
    icon: Scale,
    category: "Equipo legal",
    headline: "Para abogados internos que necesitan evidencia auditable",
    text:
      "El score por sitio + historial + audit log de scans + documentos generados con cita legal hacen que el cumplimiento sea verificable. Listo para mostrar al directorio o a la APDP.",
    bullets: ["Cada hallazgo cita Art. + literal", "Historial de scans por dominio", "Reporte exportable para gerencia"],
    cta: { label: "Ver evidencia auditable", to: "/transparencia" },
  },
];

const SECTORES = [
  { icon: ShoppingBag, label: "E-commerce" },
  { icon: HeartHandshake, label: "ONG y salud" },
  { icon: Briefcase, label: "Servicios profesionales" },
  { icon: Building2, label: "Inmobiliario" },
  { icon: Scale, label: "Educación y EdTech" },
];

export default function ThreeAudiences() {
  return (
    <section
      data-testid="three-audiences"
      className="bg-ink-950 text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-28">
        <div className="max-w-3xl mb-16">
          <p className="font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase">
            La misma plataforma · tres perfiles
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
            Construido para PYMEs.
            {' '}
            <em className="italic text-blue-300">Útil</em>
            {' '}para agencias y equipos legales también.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {AUDIENCES.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.id}
                data-testid={`audience-${a.id}`}
                className="group bg-white/[0.03] border border-white/10 rounded-2xl p-7 hover:border-blue-400/40 hover:bg-white/[0.05] transition-all"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-display text-3xl text-blue-300 leading-none">{a.id}</span>
                  <span className="font-mono text-[10px] tracking-[0.22em] text-slate-400 uppercase">
                    {a.category}
                  </span>
                </div>
                <Icon className="w-5 h-5 text-blue-300 mb-4" strokeWidth={1.5} />
                <h3 className="font-display text-xl text-white leading-snug">{a.headline}</h3>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{a.text}</p>
                <ul className="mt-5 space-y-2">
                  {a.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                      <span className="shrink-0 w-3 h-px bg-slate-600 mt-2" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={a.cta.to}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-300 hover:text-white transition-colors"
                >
                  {a.cta.label}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            );
          })}
        </div>

        {/* sectores */}
        <div className="mt-16 pt-10 border-t border-white/10">
          <p className="font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-5">
            Sectores con PYMEs analizadas
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            {SECTORES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="flex items-center gap-2 text-sm text-slate-400">
                  <Icon className="w-4 h-4 text-blue-300" strokeWidth={1.5} />
                  <span>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
